import { useMemo } from 'preact/hooks';
import type Shipment from 'interfaces/PmParcel';
import type { Carrier } from 'interfaces/Carrier.interface';
import type { CarrierOrg } from 'interfaces/CarrierOrg.interface';
import {
  getDefaultCarrier,
  buildCarrierInfo,
  formatCarrierUrlTracking,
  extractLinkedShipmentInfo,
  validateDestinationCarrier,
  computeSwitcherVisibility,
} from './useCarrierData.utils';

interface InitialCarrierState {
  initialCarrier: Carrier | undefined;
  fallbackCarrier: Carrier | undefined;
  isShowingSwitcher: boolean;
  hasLinkedShipment: boolean;
  hasDestinationCarrier: boolean;
  isLatestEventFromDestinationCarrier: boolean;
  linkedShipmentCarrier: Carrier | undefined;
  linkedShipmentCarrierOrg: any;
  destinationCarrier: Carrier | undefined;
}

interface InitalCarrierProps {
  hasLinkedShipment: boolean;
  linkedShipmentCarrier: Carrier | undefined;
  hasDestinationCarrier: boolean;
  shouldShowDestinationCarrier: boolean;
  isLatestEventFromDestinationCarrier: boolean;
  destinationCarrier: Carrier | undefined;
  fallbackCarrier: Carrier | undefined;
}
/**
 * Computes which carrier should show initially based on business priority
 * Priority: Linked-Shipment > Destination Carrier > Fallback Carrier
 * @returns Initial carrier to display
 */
const useComputeInitialCarrier = (data: InitalCarrierProps) => {
  return useMemo(() => {
    if (data.hasLinkedShipment) return data.linkedShipmentCarrier;
    if (
      data.hasDestinationCarrier &&
      data.shouldShowDestinationCarrier &&
      data.isLatestEventFromDestinationCarrier
    ) {
      return data.destinationCarrier;
    }
    return data.fallbackCarrier;
  }, [data]);
};

/**
 * Computes initial carrier selection based on business priority:
 * Linked-Shipment > Destination Carrier > Main Carrier > Org Carrier
 *
 * This hook orchestrates utility functions and React hooks to compute the initial carrier state.
 * It follows the Single Responsibility Principle by delegating specific concerns
 * to focused helper functions.
 */
export const useInitialCarrier = (
  shipment: Shipment,
  shouldShowDestinationCarrier: boolean,
): InitialCarrierState => {
  const {
    carrier,
    destinationCarrier,
    latestCarrier,
    carrierOrg,
    additionalData,
  } = shipment;

  // Step 1: Extract linked shipment information (pure function)
  const { linkedShipmentCarrier, linkedShipmentCarrierOrg, hasLinkedShipment } =
    extractLinkedShipmentInfo(additionalData);

  // Step 2: Validate destination carrier (pure function)
  const { hasDestinationCarrier, isLatestEventFromDestinationCarrier } =
    validateDestinationCarrier(destinationCarrier, latestCarrier);

  // Step 3: Compute fallback carrier (React hook - uses useMemo)
  const fallbackCarrier = getDefaultCarrier(carrier, carrierOrg);

  // Step 4: Determine switcher visibility (pure function)
  const isShowingSwitcher = computeSwitcherVisibility(
    hasLinkedShipment,
    hasDestinationCarrier,
    shouldShowDestinationCarrier,
  );

  // Step 5: Compute initial carrier based on business priority (React hook - uses useMemo)
  const initialCarrier = useComputeInitialCarrier({
    hasLinkedShipment,
    linkedShipmentCarrier,
    hasDestinationCarrier,
    shouldShowDestinationCarrier,
    isLatestEventFromDestinationCarrier,
    destinationCarrier,
    fallbackCarrier,
  });

  return {
    initialCarrier,
    fallbackCarrier,
    isShowingSwitcher,
    hasLinkedShipment,
    hasDestinationCarrier,
    isLatestEventFromDestinationCarrier,
    linkedShipmentCarrier,
    linkedShipmentCarrierOrg,
    destinationCarrier,
  };
};

interface ActiveCarrierState {
  activeCarrier: Carrier | undefined;
  isActiveCarrierLinkedShipment: boolean;
  isActiveCarrierDestination: boolean;
  isCurrentCarrierAsDestinationCarrier: boolean;
  isUserViewingToggled: boolean;
}

/**
 * Computes toggled carrier logic and determines which carrier is currently active
 */
export const useActiveCarrier = (
  initialState: InitialCarrierState,
  isUserViewingToggled: boolean,
  shouldShowDestinationCarrier: boolean,
): ActiveCarrierState => {
  const {
    initialCarrier,
    fallbackCarrier,
    isShowingSwitcher,
    hasLinkedShipment,
    hasDestinationCarrier,
    isLatestEventFromDestinationCarrier,
    destinationCarrier,
  } = initialState;

  // Toggled carrier - what user sees after clicking switcher
  const toggledCarrier = useMemo(() => {
    if (!isShowingSwitcher) return undefined;

    // If initial is linked shipment or DC (from latest event), toggled is fallback carrier
    if (
      hasLinkedShipment ||
      (hasDestinationCarrier &&
        shouldShowDestinationCarrier &&
        isLatestEventFromDestinationCarrier)
    ) {
      return fallbackCarrier;
    }

    // If initial is fallback carrier but DC exists (latest event NOT from DC), toggled is DC
    if (
      hasDestinationCarrier &&
      shouldShowDestinationCarrier &&
      !isLatestEventFromDestinationCarrier
    ) {
      return destinationCarrier;
    }

    return fallbackCarrier;
  }, [
    isShowingSwitcher,
    hasLinkedShipment,
    hasDestinationCarrier,
    shouldShowDestinationCarrier,
    isLatestEventFromDestinationCarrier,
    fallbackCarrier,
    destinationCarrier,
  ]);

  // Check if user is currently viewing toggled carrier
  const isToggledView = isUserViewingToggled && !!toggledCarrier;

  // Determine which carrier is currently active
  const activeCarrier = isToggledView ? toggledCarrier : initialCarrier;

  // Carrier type flags for label text and logic
  const isActiveCarrierLinkedShipment = !isToggledView && hasLinkedShipment;

  const isCurrentCarrierAsDestinationCarrier =
    !hasLinkedShipment &&
    hasDestinationCarrier &&
    shouldShowDestinationCarrier &&
    isLatestEventFromDestinationCarrier;

  // DC is shown as initial (latest event from DC)
  const isInitialDestinationCarrier =
    !isToggledView && isCurrentCarrierAsDestinationCarrier;

  // DC is shown as toggled (user clicked switcher when latest event NOT from DC)
  const isToggledDestinationCarrier =
    isToggledView && toggledCarrier === destinationCarrier;

  const isActiveCarrierDestination =
    isInitialDestinationCarrier || isToggledDestinationCarrier;

  return {
    activeCarrier,
    isActiveCarrierLinkedShipment,
    isActiveCarrierDestination,
    isCurrentCarrierAsDestinationCarrier,
    isUserViewingToggled: isToggledView,
  };
};

/**
 * Resolves carrier organization config based on active carrier
 * Applies OCC overrides only when carrier IDs match
 */
export const useCarrierOverride = (
  activeCarrier: Carrier | undefined,
  carrierOrg: any,
  isActiveCarrierLinkedShipment: boolean,
  linkedShipmentCarrierOrg: any,
) => {
  return useMemo(() => {
    // Linked Shipment: Always use its own OCC config (already matched)
    if (isActiveCarrierLinkedShipment) return linkedShipmentCarrierOrg;

    // Check if MC/DC ID matches OCC carrier ID
    // If match: apply OCC overrides (phone, tracking URL)
    // If no match: return undefined to use carrier defaults only
    if (activeCarrier?.id === carrierOrg?.carrier?.id) {
      return carrierOrg;
    }

    return undefined;
  }, [
    isActiveCarrierLinkedShipment,
    linkedShipmentCarrierOrg,
    activeCarrier,
    carrierOrg,
  ]);
};

/**
 * Builds final carrier info with OCC overrides and populates URL placeholders
 * Handles null/undefined carrier
 */
export const useFinalCarrierInfo = (
  activeCarrier: Carrier | undefined,
  carrierOrgConfig: CarrierOrg | undefined,
  activeParcelId: string,
  shipment: Shipment,
): Carrier | undefined => {
  return useMemo(() => {
    const baseCarrierInfo = buildCarrierInfo(activeCarrier, carrierOrgConfig);

    if (!baseCarrierInfo) return undefined;

    return {
      ...baseCarrierInfo,
      urlTracking: formatCarrierUrlTracking(
        baseCarrierInfo.urlTracking,
        activeParcelId,
        shipment,
      ),
    };
  }, [activeCarrier, carrierOrgConfig, activeParcelId, shipment]);
};
